import React, { useEffect, useState } from 'react';
import Page from '@afosto/app-components-react/Page';
import useFulfilmentLocation from '@afosto/queries/fulfilment/useFulfilmentLocation';
import useHandlingList from '@afosto/queries/fulfilment/useHandlingList';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import usePrivate from '@afosto/auth-react/usePrivate';
import { useConfirm } from '@afosto/app-components-react/ConfirmDialog';
import { useSnackbar } from 'notistack';
import { Link, Route, Switch, useHistory, useParams, useLocation } from 'react-router-dom';
import HandlingListContext from '../../components/HandlingListContext';
import HandlingListDetails from './HandlingListDetails';
import HandlingListItems from './HandlingListItems';
import HandlingListLogs from './HandlingListLogs';
import { startPicking, startPlacing } from '../../utils/handlingList';
import { handling as handlingApi } from '@afosto/fulfilment/api/Handling';
import { shipments as shipmentsApi } from '@afosto/fulfilment/api/Shipments';
import translations from './translations';

const ViewHandlingList = () => {
  usePrivate();
  const [hasPermissions, UnauthorizedComponent] = usePermissions([
    'lcs:handling:read',
    'lcs:locations:read',
  ]);

  const confirm = useConfirm();
  const intl = useIntl();
  const history = useHistory();
  const routerLocation = useLocation();
  const { handlingListID } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const [shipments, setShipments] = useState([]);
  const { data: handlingList, isLoading: isFetchingHandlingList } = useHandlingList(
    handlingListID,
    { enabled: handlingListID },
  );
  const {
    data: location,
    isLoading: isFetchingLocation,
  } = useFulfilmentLocation(handlingList?.location_id, { enabled: !!handlingList?.location_id });

  useEffect(() => {
    let isMounted = true;

    const fetchShipments = async () => {
      try {
        const promises = handlingList.shipments.map(shipmentId =>
          shipmentsApi.viewShipment(shipmentId),
        );
        const shipmentsData = await Promise.all(promises).then(shipmentResponses =>
          shipmentResponses.map(shipmentResponse => shipmentResponse?.data),
        );

        if (isMounted) {
          setShipments(shipmentsData ?? []);
        }
      } catch (err) {
        if (isMounted) {
          setShipments([]);
        }
      }
    };

    if (handlingList) {
      fetchShipments();
    }

    return () => {
      isMounted = false;
    };
  }, [handlingList]);

  const handleDeleteHandlingList = async () => {
    try {
      const confirmResponse = await confirm({
        title: intl.formatMessage(translations.deleteHandlingListDialog.title),
        description: intl.formatMessage(translations.deleteHandlingListDialog.description),
        catchOnCancel: false,
        buttons: [
          {
            label: intl.formatMessage(translations.deleteHandlingListDialog.buttons.cancel),
            type: 'cancel',
            variant: 'outlined',
            style: {
              marginRight: 'auto',
            },
          },
          {
            label: intl.formatMessage(translations.deleteHandlingListDialog.buttons.delete),
            response: 'delete',
            type: 'submit',
            variant: 'contained',
            color: 'primary',
          },
        ],
      });

      if (confirmResponse === 'delete') {
        await handlingApi.deleteHandlingList(handlingList?.id);

        enqueueSnackbar(intl.formatMessage(translations.feedback.handlingListDeleted), {
          variant: 'success',
        });

        history.push('/handling-lists');
      }
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.handlingListNotDeleted), {
        variant: 'error',
      });
    }
  };

  if (!hasPermissions()) {
    return (
      <Page title={handlingList?.number} fullWidth>
        <UnauthorizedComponent />
      </Page>
    );
  }

  let primaryAction = null;

  if (!handlingList?.is_finished) {
    if (handlingList && handlingList.is_outbound) {
      primaryAction = {
        text: intl.formatMessage(translations.actions.pick),
        action: () => startPicking({ handlingListID, history }),
        FABVariant: 'extended',
      };
    }

    if (handlingList && !handlingList.is_outbound) {
      primaryAction = {
        text: intl.formatMessage(translations.actions.place),
        action: () => startPlacing({ handlingListID, history }),
        FABVariant: 'extended',
      };
    }
  }

  const allowDelete =
    shipments?.length > 0 && !shipments.some(shipment => shipment?.parcels?.length > 0);

  const secondaryActions = [
    ...(!handlingList?.is_finished && !handlingList?.is_canceled && allowDelete
      ? [
          {
            text: intl.formatMessage(translations.secondaryActions.deleteHandlingList),
            action: handleDeleteHandlingList,
            color: 'secondary',
          },
        ]
      : []),
  ];

  return (
    <Page
      title={handlingList?.number ?? '-'}
      isLoading={isFetchingHandlingList}
      fullWidth
      subNavigation={{
        current: routerLocation.pathname,
        tabs: [
          {
            label: intl.formatMessage(translations.subNavigation.details),
            path: `/handling-lists/${handlingListID}`,
          },
          {
            label: intl.formatMessage(translations.subNavigation.items),
            path: `/handling-lists/${handlingListID}/items`,
          },
          {
            label: intl.formatMessage(translations.subNavigation.logs),
            path: `/handling-lists/${handlingListID}/logs`,
          },
        ],
        onChange: (e, value) => history.replace(value),
      }}
      breadcrumbs={[
        {
          label: intl.formatMessage(translations.breadcrumb.handlingLists),
          path: '/handling-lists',
        },
        {
          label: handlingList?.number || handlingList?.id,
          path: `/handling-lists/${handlingListID}`,
          active: true,
        },
      ]}
      linkComponent={Link}
      primaryAction={hasPermissions(['lcs:handling:write']) ? primaryAction : null}
      secondaryActions={secondaryActions}
    >
      <HandlingListContext.Provider value={{ handlingList, location, isFetchingLocation }}>
        <Switch>
          <Route path="/handling-lists/:handlingListID/logs" component={HandlingListLogs} />
          <Route path="/handling-lists/:handlingListID/items" component={HandlingListItems} />
          <Route path="/handling-lists/:handlingListID" component={HandlingListDetails} />
        </Switch>
      </HandlingListContext.Provider>
    </Page>
  );
};

export default ViewHandlingList;
